import React, { useEffect, useState } from "react";
import Modal from "../components/Modal/Modal";
import styles from "./FindingsModal.module.scss";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import Separator from "../components/Separator/Separator";
import { formatDate } from "../utils/helpers";
import SecondaryButton from "../components/Button/SecondaryButton";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { hideModal } from "../redux/modalStore";
import Table from "../components/Table/Table";
import { ReactComponent as EditIcon } from "../icons/edit.svg";
import { ReactComponent as BackArrow } from "../icons/back-arrow.svg";
import Button from "../components/Button/Button";
import { Field, Form, Formik } from "formik";
import Input from "../components/Input/Input";
import Select from "../components/Select/Select";
import Textarea from "../components/Textarea/Textarea";
import axiosInstance from "../utils/utils";
import { getOrganizationData } from "../actions/organization";
import Pill from "../components/Pill/Pill";
import * as Yup from "yup";
import ModalSideBar from "../components/ModalSideBar/ModalSideBar";
import ConfirmationPopup from "./ConfirmationPopup";
import WYSIWYGEditor from "../components/WYSIWYGEditor/WYSIWYGEditor";
import DOMPurify from "dompurify";
import { marked } from "marked";
import DatePickerField from "../components/FormikComponents/DatePickerField";
import ClosedLockIcon, { ReactComponent as LockIcon } from "../icons/lock.svg";
import PublicIcon from "../icons/public.svg";
import WarningIllustration from "../icons/no-data.svg";
import IconSelect from "../components/IconSelect/IconSelect";
import { type } from "@testing-library/user-event/dist/type";
import {
  clearModalHistory,
  popModal,
  pushModal,
} from "../redux/modalNavigationStore";

const FindingsModal = ({ findingData, isLoading }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const { findingKey } = useParams();
  const sessionToken = localStorage.getItem("session_token");
  const location = useLocation();

  const { linkedData, risksData } = useSelector((state) => state.organization);
  const openedModal = useSelector((state) => state.modal.openedModal);
  const user = useSelector((state) => state.user.user);
  const modalHistory = useSelector((state) => state.modalNavigation.history);

  const [editField, setEditField] = useState(null);
  const [editableData, setEditableData] = useState(findingData || {});
  const [unsavedChanges, setUnsavedChanges] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [updates, setUpdates] = useState(openedModal?.data?.updates || []);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [isRiskUpdating, setRiskUpdating] = useState(false);

  useEffect(() => {
    setEditableData(findingData || {});
    setUnsavedChanges({});
    setEditField(null);
    setLoading(false);
    setUpdates(openedModal?.data?.updates || []);
  }, [findingData, openedModal?.data]);

  useEffect(() => {
    // Add the event listener when the component mounts
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup the event listener when the component unmounts
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!findingKey) {
      dispatch(hideModal());
    }
  }, [findingKey, dispatch]);

  const handleOnClose = (dirty, resetForm) => {
    if (dirty) {
      setShowConfirmationPopup(true);
    } else {
      navigate(`/${orgId}/findings${location.search}`, { replace: true });
      dispatch(hideModal());
      dispatch(clearModalHistory());

      // Ensure resetForm is defined before calling it
      if (resetForm && typeof resetForm === "function") {
        resetForm();
      }

      // Reset other states after closing the modal
      setEditableData(findingData || {});
      setEditField(null);
    }
  };

  const handleConfirmClose = (resetForm) => {
    setShowConfirmationPopup(false);

    navigate(`/${orgId}/findings${location.search}`, { replace: true });

    dispatch(hideModal());
    dispatch(clearModalHistory());
    resetForm();
  };

  const handleCancel = () => {
    setShowConfirmationPopup(false);
  };

  const handleNavigationClick = (path) => {
    dispatch(hideModal());
    navigate(`/${orgId}/${path}`, { replace: true });
  };

  const handleFieldChange = (field, value) => {
    const newValue = value === "" ? (field === "responsible" ? "" : []) : value;
    setUnsavedChanges((prevChanges) => ({
      ...prevChanges,
      [field]: newValue,
    }));
    setEditableData((prevData) => ({
      ...prevData,
      [field]: newValue,
    }));
  };

  const handleSave = async (values, resetForm) => {
    const sanitizeData = (data) => {
      return Object.keys(data).reduce((acc, key) => {
        if (Array.isArray(data[key])) {
          // If the array is empty or contains only empty strings, send it as an empty array
          acc[key] =
            data[key].filter((item) =>
              typeof item === "string" ? item.trim() !== "" : item,
            ) || [];
        } else if (typeof data[key] === "string") {
          // Allow empty strings to be sent
          acc[key] = data[key].trim();
        } else if (key === "date" && data[key] instanceof Date) {
          // Date is in "YYYY-MM-DD" format
          acc[key] = data[key].toISOString().split("T")[0];
        } else {
          // For other types, retain as is or set to null if explicitly undefined
          acc[key] = data[key] ?? "";
        }
        return acc;
      }, {});
    };

    const sanitizedValues = sanitizeData(values);
    const sanitizedOriginalData = sanitizeData(findingData);

    const changedFields = {};
    for (const key in sanitizedValues) {
      if (key === "responsible") {
        if (sanitizedValues[key] !== sanitizedOriginalData[key]) {
          changedFields[key] = sanitizedValues[key];
        }
      } else if (
        typeof sanitizedValues[key] === "object" &&
        sanitizedValues[key] !== null &&
        !Array.isArray(sanitizedValues[key])
      ) {
        // For objects, compare their JSON string representations
        if (
          JSON.stringify(sanitizedValues[key]) !==
          JSON.stringify(sanitizedOriginalData[key])
        ) {
          changedFields[key] = sanitizedValues[key];
        }
      } else if (Array.isArray(sanitizedValues[key])) {
        // Ensure both values are arrays before comparing with sort
        const sortedSanitizedValues = [...sanitizedValues[key]].sort(); // Create a shallow copy before sorting
        const sortedOriginalData = Array.isArray(sanitizedOriginalData[key])
          ? [...sanitizedOriginalData[key]].sort() // Create a shallow copy before sorting
          : [];
        if (
          JSON.stringify(sortedSanitizedValues) !==
          JSON.stringify(sortedOriginalData)
        ) {
          changedFields[key] = sanitizedValues[key];
        }
      } else {
        // For primitive values (string, number, etc.)
        if (sanitizedValues[key] !== sanitizedOriginalData[key]) {
          changedFields[key] = sanitizedValues[key];
        }
      }
    }
    if (Object.keys(changedFields).length === 0) {
      return;
    }

    const payload = {
      id: findingData?.id,
      ...changedFields,
    };
    setLoading(true);
    setLoading(true);
    if ("risk" in changedFields) {
      setRiskUpdating(true);
    }

    try {
      const response = await axiosInstance.patch(
        `/nocodb/findings?org_id=${orgId}`,
        [payload],
        {
          headers: {
            "Content-Type": "application/json",
            "session-token": sessionToken,
          },
        },
      );

      // Update the local state with the saved changes
      setEditableData((prevData) => ({
        ...prevData,
        ...changedFields,
      }));

      setUnsavedChanges({});
      setEditField(null);

      // Fetch the updated organization data
      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          true,
          true,
          true,
          false,
          false,
        ),
      );

      // Reset the form after data is fetched successfully, with a slight delay
      setTimeout(() => {
        resetForm(); // Ensure form resets after everything is done
      }, 0);
    } catch (error) {
      console.error("There was a problem with the save operation:", error);
    } finally {
      setLoading(false);
      setRiskUpdating(false);
      handleOnClose(); // Close the modal after saving and fetching data
    }
  };

  const isEditable = !findingData?.private || findingData?.summary !== "Hidden";

  const truncateText = (text, maxLength) => {
    if (!text) {
      // If text is null, undefined, or an empty string
      return "There is no description provided";
    }

    if (text.length <= maxLength) {
      return text;
    }

    return (
      <>
        {text.substring(0, maxLength)}
        <span className={styles.readMore}>...</span>
      </>
    );
  };

  const simpleTruncateText = (text, maxLength) => {
    if (text.length <= maxLength) {
      return text;
    }
    return (
      <>
        {text.substring(0, maxLength)}
        <span>...</span>
      </>
    );
  };

  let riskColor;
  switch (findingData?.risk?.level) {
    case "high":
      riskColor = styles.highLevel;
      break;
    case "medium":
      riskColor = styles.mediumLevel;
      break;
    case "low":
      riskColor = styles.lowLevel;
      break;
    default:
      riskColor = styles.unknownLevel;
  }

  const statusOptions = [
    {
      id: 1,
      label: "Open",
    },
    {
      id: 2,
      label: "In Progress",
    },
    {
      id: 3,
      label: "In Review",
    },
    {
      id: 4,
      label: "Done",
    },
  ];

  // Helper function to get the label from statusOptions based on the value
  const getStatusLabel = (statusValue) => {
    const statusOption = statusOptions.find(
      (option) => option.label === statusValue,
    );
    return statusOption ? statusOption.label : statusValue;
  };

  const validationSchema = Yup.object().shape({
    responsible: Yup.string().test(
      "is-valid",
      "Invalid email or name",
      (value) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const nameRegex = /^[a-zA-Z .]+$/; // Allows letters, spaces, and dots
        return emailRegex.test(value) || nameRegex.test(value);
      },
    ),
    date: Yup.date().required("Date is required"),
  });

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setEditField(null); // Reset editField when Esc is pressed
    }
  };

  const handleLinkedModalOpen = (path) => {
    dispatch(pushModal(window.location.pathname)); // use real current path
    dispatch(hideModal());
    navigate(`/${orgId}/${path}`); // Open new modal
  };

  const handleGoBack = () => {
    if (modalHistory.length > 0) {
      const lastPath = modalHistory[modalHistory.length - 1];
      dispatch(popModal());
      navigate(lastPath);
    } else {
      navigate(-1); // fallback to browser history
    }
  };

  const linkedImprovementsColumns = (navigate, orgId) => [
    {
      accessorKey: "key",
      header: "Key",
      size: 90,
      cell: (props) => {
        const key = props.getValue();
        return key ? (
          <a
            href={`/${orgId}/improvement-projects/${key}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleLinkedModalOpen(`improvement-projects/${key}`);
            }}
            className={styles.link}
          >
            {key}
          </a>
        ) : (
          <div style={{ color: "gray", fontStyle: "italic" }}>
            Key not available
          </div>
        );
      },
    },
    {
      accessorKey: "name",
      header: "Name",
      size: 135,
      cell: (props) => <div>{simpleTruncateText(props.getValue(), 40)}</div>,
    },
    {
      accessorKey: "description",
      header: "Description",
      size: 350,
      cell: (props) => <div>{truncateText(props.getValue(), 80)}</div>,
    },
    {
      accessorKey: "responsible",
      header: "Responsible",
      size: 200,
      cell: (props) => {
        return props.row.original.responsible ? (
          <div>{props.row.original.responsible}</div>
        ) : (
          <div>Not defined</div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 120,
      cell: (props) => {
        const status = props.getValue();
        let statusClassName = "";

        switch (status) {
          case "Open":
            statusClassName = styles.openStatus;
            break;
          case "In Progress":
            statusClassName = styles.inProgressStatus;
            break;
          case "Done":
            statusClassName = styles.resolvedStatus;
            break;
          default:
            break;
        }

        return (
          <div className={classNames(styles.statusCell, statusClassName)}>
            {status}
          </div>
        );
      },
    },
  ];

  const linkedAuditTasksColumns = (navigate, orgId) => [
    {
      accessorKey: "key",
      header: "Key",
      size: 90,
      cell: (props) => {
        const key = props.getValue();
        return key ? (
          <a
            href={`/${orgId}/audits/${key}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleLinkedModalOpen(`audits/${key}`);
            }}
            className={styles.link}
          >
            {key}
          </a>
        ) : (
          <div style={{ color: "gray", fontStyle: "italic" }}>
            Key not available
          </div>
        );
      },
    },
    {
      accessorKey: "name",
      header: "Name",
      size: 135,
      cell: (props) => <div>{simpleTruncateText(props.getValue(), 40)}</div>,
    },
    {
      accessorKey: "description",
      header: "Description",
      size: 350,
      cell: (props) => <div>{truncateText(props.getValue(), 80)}</div>,
    },
    {
      accessorKey: "responsible",
      header: "Responsible",
      size: 200,
      cell: (props) => {
        return props.row.original.responsible ? (
          <div>{props.row.original.responsible}</div>
        ) : (
          <div>Not defined</div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 120,
      cell: (props) => {
        const status = props.getValue();
        let statusClassName = "";

        switch (status) {
          case "Open":
            statusClassName = styles.openStatus;
            break;
          case "In Progress":
            statusClassName = styles.inProgressStatus;
            break;
          case "In Review":
            statusClassName = styles.inReviewStatus;
            break;
          case "Done":
            statusClassName = styles.resolvedStatus;
            break;
          default:
            break;
        }

        return (
          <div className={classNames(styles.statusCell, statusClassName)}>
            {status}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Formik
        key={findingData?.id}
        initialValues={{
          ...editableData,
          riskLabel: editableData.risk
            ? `${editableData.risk.risk_id} - ${editableData.risk.description}`
            : "",
          date: editableData.date ? new Date(editableData.date) : "",
        }}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          // Find the risk number based on the selected label
          const selectedRisk = Object.values(risksData).find(
            (risk) =>
              `${risk.risk_id} - ${risk.description}` === values.riskLabel,
          );

          const modifiedValues = {
            ...values,
          };

          // Remove the riskLabel before sending to handleSave
          delete modifiedValues.riskLabel;

          // Only include `risk` if it's changed
          const originalRiskNum = findingData?.risk?.risk_num
            ? String(findingData.risk.risk_num)
            : "";

          const newRiskNum = selectedRisk ? String(selectedRisk.risk_num) : "";

          if (newRiskNum !== originalRiskNum) {
            modifiedValues.risk = newRiskNum;
          }

          handleSave(modifiedValues, resetForm); // Pass the modified values to handleSave
        }}
      >
        {({
          values,
          setFieldValue,
          dirty,
          resetForm,
          submitForm,
          isSubmitting,
        }) => {
          return (
            <>
              <Modal
                key={findingData?.id}
                name="findings_modal"
                title={
                  <div className={styles.modalHeader}>
                    {modalHistory.length > 0 && (
                      <SecondaryButton
                        text="Go back"
                        onClick={handleGoBack}
                        className={styles.backButton}
                        icon={<BackArrow />}
                        iconPosition="left"
                      />
                    )}
                    <div className={styles.titleKey}>
                      {findingData?.private && (
                        <LockIcon
                          className={styles.lockIcon}
                          title="This finding is private"
                        />
                      )}
                      {findingData?.key}
                    </div>
                  </div>
                }
                onClose={() => handleOnClose(dirty, resetForm)}
                width="95vw"
                height="95vh"
                headerAction={
                  isEditable ? (
                    <Button
                      text={loading ? "Saving..." : "Save Changes"}
                      onClick={submitForm} // Trigger Formik's submit
                      className={styles.submitButton}
                      disabled={!dirty || isSubmitting} // Disable if no changes or submitting
                    />
                  ) : null
                }
              >
                <div className={styles.modalContentContainer}>
                  <div className={styles.modalContent}>
                    <Form className={styles.content}>
                      <div className={styles.header}>
                        <div className={styles.leftSide}>
                          <div className={styles.row}>
                            <div className={styles.title}>Reporter:</div>
                            <div className={styles.bold}>
                              {findingData?.reporter}
                            </div>
                          </div>
                          <div className={styles.row}>
                            <div className={styles.title}>Date:</div>
                            {editField === "date" ? (
                              <Field
                                name="date"
                                component={DatePickerField}
                                onChange={(value) => {
                                  setFieldValue("date", value);
                                  handleFieldChange("date", value);
                                }}
                              />
                            ) : (
                              <div className={styles.dateContainer}>
                                <div className={styles.bold}>
                                  {formatDate(values.date || editableData.date)}
                                </div>
                                {isEditable && (
                                  <EditIcon
                                    className={styles.editIcon}
                                    onClick={() => setEditField("date")}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className={styles.rightSide}>
                          <div className={styles.row}>
                            <div className={styles.title}>Status:</div>
                            <div className={styles.statusContainer}>
                              {editField === "status" ? (
                                <Field
                                  name="status"
                                  component={Select}
                                  options={statusOptions}
                                  value={values.status || editableData?.status}
                                  onChange={(e) => {
                                    const selectedOption = e.target.value;
                                    setFieldValue("status", selectedOption);
                                    handleFieldChange("status", selectedOption);
                                    setEditField(null);
                                  }}
                                >
                                  {getStatusLabel(
                                    values.status || editableData?.status,
                                  )}
                                </Field>
                              ) : (
                                <>
                                  <div
                                    className={classNames(
                                      styles.statusCell,
                                      values.status === "In Progress"
                                        ? styles.inProgressStatus
                                        : values.status === "In Review"
                                          ? styles.inReviewStatus
                                          : values.status === "Done"
                                            ? styles.resolvedStatus
                                            : styles.openStatus,
                                    )}
                                  >
                                    {values.status || editableData?.status}
                                  </div>
                                  {isEditable && (
                                    <EditIcon
                                      className={styles.editIcon}
                                      onClick={() => setEditField("status")}
                                    />
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          {!isSidebarOpen && (
                            <SecondaryButton
                              text="See Updates"
                              onClick={() => setSidebarOpen(true)}
                              className={styles.toggleSidebarButton}
                              type="Button"
                            />
                          )}
                        </div>
                      </div>
                      <Separator />
                      {(user?.Email === findingData?.responsible ||
                        user?.Role === "QMSAdmin" ||
                        user?.Role === "Consultant") && (
                        <>
                          <div className={styles.row}>
                            <div className={styles.title}>Privacy:</div>
                            {editField === "private" ? (
                              <IconSelect
                                name="private"
                                options={[
                                  {
                                    label: "Private Finding",
                                    value: true,
                                    icon: ClosedLockIcon,
                                  },
                                  {
                                    label: "Public Finding",
                                    value: false,
                                    icon: PublicIcon,
                                  },
                                ]}
                                placeholder="Select privacy"
                              />
                            ) : (
                              <div className={styles.privacyDisplay}>
                                <img
                                  src={
                                    values.private ? ClosedLockIcon : PublicIcon
                                  }
                                  alt={values.private ? "Private" : "Public"}
                                  className={styles.privacyIcon}
                                />
                                <span>
                                  {values.private
                                    ? "Private Finding"
                                    : "Public Finding"}
                                </span>
                                {isEditable && (
                                  <EditIcon
                                    className={styles.editIcon}
                                    onClick={() => setEditField("private")}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                          <Separator />
                        </>
                      )}
                      <>
                        <div className={styles.row}>
                          <div className={styles.title}>Risk Level:</div>
                          {isRiskUpdating ? (
                            <div className={styles.bold}>Updating risk...</div>
                          ) : (
                            <div
                              className={classNames(
                                styles.riskLevel,
                                editableData?.risk?.level && riskColor,
                              )}
                            >
                              {editableData?.risk?.level || "Undefined"}
                            </div>
                          )}
                        </div>

                        <div className={styles.row}>
                          <div className={styles.title}>Risk:</div>
                          {editField === "risk" ? (
                            <>
                              <Field
                                name="riskLabel"
                                component={Select}
                                className={styles.select}
                                text="Select risk"
                                options={Object.values(risksData)
                                  .filter(
                                    (risk) =>
                                      risk.level?.toLowerCase?.() !== "none",
                                  )
                                  .sort((a, b) =>
                                    a.risk_id.localeCompare(b.risk_id),
                                  )
                                  .map((risk) => ({
                                    value: `${risk.risk_id} - ${risk.description}`,
                                    label: `${risk.risk_id} - ${risk.description}`,
                                  }))}
                                onChange={(e) => {
                                  setFieldValue("riskLabel", e.target.value); // Store the selected label
                                  setEditField(null);
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <div className={styles.bold}>
                                {isRiskUpdating
                                  ? "Updating risk..."
                                  : findingData?.risk
                                    ? `${editableData?.risk?.risk_id} - ${editableData?.risk?.description}`
                                    : "Undefined"}
                              </div>
                              {isEditable && (
                                <EditIcon
                                  className={styles.editIcon}
                                  onClick={() => setEditField("risk")}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </>
                      <div className={styles.tableContainer}>
                        <table className={styles.teamRolesTable}>
                          <tbody>
                            {/* Responsible */}
                            <tr>
                              <td>Responsible</td>
                              <td>
                                {editField === "responsible" ? (
                                  <Field
                                    name="responsible"
                                    component={Input}
                                    placeholder="Enter responsible email"
                                  />
                                ) : (
                                  <div>
                                    {Array.isArray(values.responsible)
                                      ? values.responsible.join(", ")
                                      : values.responsible || ""}
                                  </div>
                                )}
                              </td>
                              {isEditable && (
                                <td>
                                  <EditIcon
                                    className={styles.editIcon}
                                    onClick={() => setEditField("responsible")}
                                  />
                                </td>
                              )}
                            </tr>
                            {/* Accountable */}
                            <tr>
                              <td>Accountable</td>
                              <td>
                                {editField === "accountable" ? (
                                  <Field
                                    name="accountable"
                                    component={Pill}
                                    placeholder="Enter accountable emails"
                                    limit={5}
                                  />
                                ) : (
                                  (values.accountable || []).map(
                                    (email, index) => (
                                      <div key={index}>{email}</div>
                                    ),
                                  ) || "Not defined"
                                )}
                              </td>
                              {isEditable && (
                                <td>
                                  <EditIcon
                                    className={styles.editIcon}
                                    onClick={() => setEditField("accountable")}
                                  />
                                </td>
                              )}
                            </tr>
                            {/* Consulted */}
                            <tr>
                              <td>Consulted</td>
                              <td>
                                {editField === "consulted" ? (
                                  <Field
                                    name="consulted"
                                    component={Pill}
                                    placeholder="Enter consulted emails"
                                    limit={5}
                                  />
                                ) : (
                                  (values.consulted || []).map(
                                    (email, index) => (
                                      <div key={index}>{email}</div>
                                    ),
                                  ) || "Not defined"
                                )}
                              </td>
                              {isEditable && (
                                <td>
                                  <EditIcon
                                    className={styles.editIcon}
                                    onClick={() => setEditField("consulted")}
                                  />
                                </td>
                              )}
                            </tr>
                            {/* Informed */}
                            <tr>
                              <td>Informed</td>
                              <td>
                                {editField === "informed" ? (
                                  <Field
                                    name="informed"
                                    component={Pill}
                                    placeholder="Enter informed emails"
                                    limit={5}
                                  />
                                ) : (
                                  (values.informed || []).map(
                                    (email, index) => (
                                      <div key={index}>{email}</div>
                                    ),
                                  ) || "Not defined"
                                )}
                              </td>
                              {isEditable && (
                                <td>
                                  <EditIcon
                                    className={styles.editIcon}
                                    onClick={() => setEditField("informed")}
                                  />
                                </td>
                              )}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <Separator />
                      {/*Summary*/}
                      {values.summary && findingData?.status === "Done" && (
                        <>
                          <div className={styles.title}>Summary:</div>
                          {editField === "summary" ? (
                            <div className={styles.editorContainer}>
                              <Field
                                name="summary"
                                component={WYSIWYGEditor}
                                placeholder="Edit the summary..."
                              />
                            </div>
                          ) : (
                            <div className={styles.summaryContent}>
                              {isEditable && (
                                <EditIcon
                                  className={styles.editIcon}
                                  onClick={() => setEditField("summary")}
                                />
                              )}
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    marked.parse(values.summary.trim()),
                                  ),
                                }}
                              />
                            </div>
                          )}
                          <Separator />
                        </>
                      )}
                      {/*Reference*/}
                      <div className={styles.row}>
                        <div className={styles.title}>Reference:</div>
                        {editField === "reference" ? (
                          <Field
                            name="reference"
                            component={Input}
                            className={styles.bold}
                          />
                        ) : (
                          <>
                            <div className={styles.bold}>
                              {values.reference || editableData?.reference
                                ? values.reference || editableData?.reference
                                : "Not Defined"}
                            </div>
                            {isEditable && (
                              <EditIcon
                                className={styles.editIcon}
                                onClick={() => setEditField("reference")}
                              />
                            )}
                          </>
                        )}
                      </div>

                      <div className={styles.title}>Description:</div>
                      {editField === "description" ? (
                        <Field name="description" component={Textarea} />
                      ) : (
                        <div className={styles.description}>
                          <div>
                            {values.description || editableData?.description
                              ? values.description || editableData?.description
                              : "Not Defined"}
                          </div>
                          {isEditable && (
                            <EditIcon
                              className={styles.editIcon}
                              onClick={() => setEditField("description")}
                            />
                          )}
                        </div>
                      )}
                      {linkedData?.linked_improvements?.length > 0 && (
                        <>
                          <Separator />
                          <div className={styles.title}>
                            Linked Improvements
                          </div>
                          <div className={styles.tableContainer}>
                            <Table
                              columns={linkedImprovementsColumns(
                                navigate,
                                orgId,
                              )}
                              data={linkedData.linked_improvements}
                            />
                          </div>
                          <SecondaryButton
                            text="View all improvements..."
                            className={styles.secondaryButton}
                            onClick={() =>
                              handleNavigationClick("improvement-projects")
                            }
                          />
                        </>
                      )}
                      {linkedData?.linked_auditTasks?.length > 0 && (
                        <>
                          <Separator />
                          <div className={styles.title}>Linked Audit Tasks</div>
                          <div className={styles.tableContainer}>
                            <Table
                              columns={linkedAuditTasksColumns(navigate, orgId)}
                              data={linkedData.linked_auditTasks}
                            />
                          </div>
                          <SecondaryButton
                            text="View all audit tasks..."
                            className={styles.secondaryButton}
                            onClick={() => handleNavigationClick("audits")}
                          />
                        </>
                      )}
                      {findingData?.attachments.length > 0 && (
                        <>
                          <div className={styles.title}>Attachments:</div>
                          <div className={styles.attachments}>
                            {findingData?.attachments.map((attachment) => (
                              <a
                                key={attachment}
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                className={styles.attachmentItem}
                              >
                                {attachment.filename}
                              </a>
                            ))}
                          </div>
                        </>
                      )}
                    </Form>
                  </div>
                  <ModalSideBar
                    isOpen={isSidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                    updates={updates}
                    setUpdates={setUpdates}
                    findingId={openedModal?.data?.id}
                  />
                </div>
              </Modal>
              {showConfirmationPopup && (
                <ConfirmationPopup
                  message="Your changes won’t be saved if you close this window."
                  illustration={WarningIllustration}
                  confirmText="Close Without Saving"
                  onConfirm={() => handleConfirmClose(resetForm)}
                  onCancel={handleCancel}
                />
              )}
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default FindingsModal;
